import { useEffect, useState } from "react";

/**
 * Sentry-lite — monkey-patches `console.error` / `console.warn` and captures
 * `window.error` / `unhandledrejection` events into a ring buffer that the
 * QAConsole can show to exempt users in real time.
 *
 * Returns: { entries, count, clear } — entries is newest-first.
 */

const MAX_ENTRIES = 50;
const _store = {
  entries: [],   // [{ id, level, ts, message, source }]
  listeners: new Set(),
  installed: false,
};

const _push = (level, args, source = "console") => {
  const msg = args.map((a) => {
    if (a instanceof Error) return a.message + (a.stack ? `\n${a.stack.split("\n").slice(0, 3).join("\n")}` : "");
    if (typeof a === "object") {
      try { return JSON.stringify(a, null, 2).slice(0, 500); } catch { return String(a); }
    }
    return String(a);
  }).join(" ");
  // Skip framework noise that's not actionable for the user
  if (/ResizeObserver|hydration|Download the React DevTools|Warning: Each child/i.test(msg)) return;
  const entry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    level, ts: new Date(), message: msg.slice(0, 1000), source,
  };
  _store.entries.unshift(entry);
  if (_store.entries.length > MAX_ENTRIES) _store.entries.length = MAX_ENTRIES;
  // Note: listener catches MUST stay silent — they run inside the intercepted
  // console.error/warn path; logging here would cause infinite recursion.
  _store.listeners.forEach((fn) => { try { fn(); } catch (_e) { /* recursion-safe */ } });
};

const installOnce = () => {
  if (_store.installed) return;
  _store.installed = true;
  const origError = console.error.bind(console);
  const origWarn = console.warn.bind(console);
  console.error = (...args) => { _push("error", args); origError(...args); };
  console.warn  = (...args) => { _push("warn",  args); origWarn(...args);  };
  window.addEventListener("error", (e) => {
    _push("error", [e?.error || e?.message || "window.error"], "window");
  });
  window.addEventListener("unhandledrejection", (e) => {
    _push("error", [e?.reason || "unhandled rejection"], "promise");
  });
};

export function useErrorSink() {
  const [, setTick] = useState(0);
  useEffect(() => {
    installOnce();
    const fn = () => setTick((t) => t + 1);
    _store.listeners.add(fn);
    return () => { _store.listeners.delete(fn); };
  }, []);
  return {
    entries: _store.entries,
    count: _store.entries.length,
    errorCount: _store.entries.filter((e) => e.level === "error").length,
    warnCount: _store.entries.filter((e) => e.level === "warn").length,
    clear: () => {
      _store.entries.length = 0;
      _store.listeners.forEach((fn) => { try { fn(); } catch (_e) { /* recursion-safe */ } });
    },
  };
}
